import { useState, useCallback } from "react";
import { ChevronDown, ChevronRight, Download, RefreshCw } from "lucide-react";
import { runSensitivity, type SensitivityRequest, type SensitivityResponse } from "../api/client";
import { fmtPercent, fmtCurrencyCompact, fmtMultiple } from "../utils/format";
import { downloadCSV } from "../utils/csv";
import { friendlyError } from "../utils/errors";

interface SensitivityAnalysisProps {
  dealId: string;
  scenarioId: string;
  currentAssumptions: {
    exit_cap_rate: number;
    purchase_price: number;
    general_vacancy_pct: number;
    expense_growth_rate_pct: number;
    market_rent_growth_pct_annual: number;
    discount_rate: number;
  };
}

const INPUT_OPTIONS = [
  { value: "exit_cap_rate", label: "Exit Cap Rate", fmt: "pct", step: 0.005 },
  { value: "purchase_price", label: "Purchase Price", fmt: "dollar", step: 500000 },
  { value: "vacancy_rate", label: "Vacancy Rate", fmt: "pct", step: 0.01 },
  { value: "expense_growth", label: "Expense Growth", fmt: "pct", step: 0.005 },
  { value: "market_rent_growth", label: "Mkt Rent Growth", fmt: "pct", step: 0.005 },
  { value: "discount_rate", label: "Discount Rate", fmt: "pct", step: 0.01 },
];

const OUTPUT_OPTIONS = [
  { value: "irr_unlevered", label: "Unlevered IRR", fmt: "pct2" },
  { value: "irr_levered", label: "Levered IRR", fmt: "pct2" },
  { value: "equity_multiple", label: "Equity Multiple", fmt: "x2" },
  { value: "noi_year1", label: "Year 1 NOI", fmt: "dollar" },
  { value: "npv", label: "NPV", fmt: "dollar" },
  { value: "exit_price", label: "Exit Price", fmt: "dollar" },
  { value: "avg_dscr", label: "Avg DSCR", fmt: "x2" },
  { value: "avg_cash_on_cash", label: "Avg Cash-on-Cash", fmt: "pct1" },
];

const CURRENT_MAP: Record<string, string> = {
  exit_cap_rate: "exit_cap_rate",
  purchase_price: "purchase_price",
  vacancy_rate: "general_vacancy_pct",
  expense_growth: "expense_growth_rate_pct",
  market_rent_growth: "market_rent_growth_pct_annual",
  discount_rate: "discount_rate",
};

function getDefaults(variable: string, current: number) {
  const opt = INPUT_OPTIONS.find(o => o.value === variable);
  if (!opt) return { min: 0, max: 0.1, step: 0.01 };
  if (opt.fmt === "dollar") {
    const step = current > 5_000_000 ? 1_000_000 : 500_000;
    return { min: Math.max(0, Math.round(current * 0.7 / step) * step), max: Math.round(current * 1.3 / step) * step, step };
  }
  return { min: Math.max(0, round6(current - opt.step * 3)), max: round6(current + opt.step * 3), step: opt.step };
}

function round6(v: number) { return Math.round(v * 1e6) / 1e6; }

function fmtInputVal(v: number, variable: string): string {
  const opt = INPUT_OPTIONS.find(o => o.value === variable);
  if (!opt) return String(v);
  if (opt.fmt === "dollar") return fmtCurrencyCompact(v);
  // Cap and discount rates carry 2 decimals; vacancy/growth rates 1.
  const decimals = variable === "exit_cap_rate" || variable === "discount_rate" ? 2 : 1;
  return fmtPercent(v, decimals);
}

function fmtOutputVal(v: number | null, metric: string): string {
  if (v == null) return "—";
  const opt = OUTPUT_OPTIONS.find(o => o.value === metric);
  if (!opt) return String(v);
  if (opt.fmt === "pct2") return fmtPercent(v, 2);
  if (opt.fmt === "pct1") return fmtPercent(v, 1);
  if (opt.fmt === "x2") return fmtMultiple(v);
  if (opt.fmt === "dollar") return fmtCurrencyCompact(v);
  return String(v);
}

function cellStyle(v: number | null, min: number, max: number): React.CSSProperties {
  if (v == null || max === min) return { backgroundColor: "#FAFAF5" };
  const ratio = Math.max(0, Math.min(1, (v - min) / (max - min)));
  const r = Math.round(200 - ratio * 166);
  const g = Math.round(60 + ratio * 60);
  const b = Math.round(50 - ratio * 10);
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return { backgroundColor: `rgb(${r},${g},${b})`, color: lum < 140 ? "#fff" : "#2C2C28" };
}

export default function SensitivityAnalysis({ dealId, scenarioId, currentAssumptions }: SensitivityAnalysisProps) {
  const [open, setOpen] = useState(false);
  const [rowVar, setRowVar] = useState("exit_cap_rate");
  const [colVar, setColVar] = useState("");
  const [outputMetric, setOutputMetric] = useState("irr_unlevered");
  const [rowMin, setRowMin] = useState("");
  const [rowMax, setRowMax] = useState("");
  const [rowStep, setRowStep] = useState("");
  const [colMin, setColMin] = useState("");
  const [colMax, setColMax] = useState("");
  const [colStep, setColStep] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SensitivityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getCurrentVal = useCallback((variable: string) => {
    const field = CURRENT_MAP[variable];
    return field ? (currentAssumptions as Record<string, number>)[field] ?? 0 : 0;
  }, [currentAssumptions]);

  const handleVarChange = useCallback((setter: "row" | "col", value: string) => {
    const current = getCurrentVal(value);
    const d = getDefaults(value, current);
    const opt = INPUT_OPTIONS.find(o => o.value === value);
    const isDollar = opt?.fmt === "dollar";
    const fmtD = (v: number) => isDollar ? v.toLocaleString() : String(round6(v * 100));
    if (setter === "row") {
      setRowVar(value);
      setRowMin(fmtD(d.min));
      setRowMax(fmtD(d.max));
      setRowStep(fmtD(d.step));
    } else {
      setColVar(value);
      setColMin(fmtD(d.min));
      setColMax(fmtD(d.max));
      setColStep(fmtD(d.step));
    }
  }, [getCurrentVal]);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const rowOpt = INPUT_OPTIONS.find(o => o.value === rowVar);
      const isDollarRow = rowOpt?.fmt === "dollar";
      const pn = (s: string) => parseFloat(s.replace(/,/g, ""));
      const req: SensitivityRequest = {
        row_variable: rowVar,
        row_min: isDollarRow ? pn(rowMin) : pn(rowMin) / 100,
        row_max: isDollarRow ? pn(rowMax) : pn(rowMax) / 100,
        row_step: isDollarRow ? pn(rowStep) : pn(rowStep) / 100,
        output_metric: outputMetric,
      };
      if (colVar) {
        const colOpt = INPUT_OPTIONS.find(o => o.value === colVar);
        const isDollarCol = colOpt?.fmt === "dollar";
        req.col_variable = colVar;
        req.col_min = isDollarCol ? pn(colMin) : pn(colMin) / 100;
        req.col_max = isDollarCol ? pn(colMax) : pn(colMax) / 100;
        req.col_step = isDollarCol ? pn(colStep) : pn(colStep) / 100;
      }
      const resp = await runSensitivity(dealId, scenarioId, req);
      setResult(resp);
    } catch (e: any) {
      setError(friendlyError(e?.response?.data?.detail || "Couldn't run the sensitivity analysis. Try again."));
    } finally {
      setLoading(false);
    }
  };

  // Auto-set defaults when section opens
  const handleToggle = () => {
    if (!open && !rowMin) handleVarChange("row", rowVar);
    setOpen(!open);
  };

  const exportResults = () => {
    if (!result) return;
    const is2D = result.col_values && result.col_values.length > 0;
    const headers = is2D
      ? [result.row_variable, ...result.col_values!.map(v => fmtInputVal(v, result.col_variable!))]
      : [result.row_variable, OUTPUT_OPTIONS.find(o => o.value === outputMetric)?.label || outputMetric];
    const rows = result.row_values.map((rv, ri) => [
      fmtInputVal(rv, result.row_variable),
      ...result.results[ri].map(v => fmtOutputVal(v, outputMetric)),
    ]);
    downloadCSV([headers, ...rows], "sensitivity_analysis.csv");
  };

  const needsPurchasePrice = ["irr_unlevered", "irr_levered", "equity_multiple", "npv", "avg_cash_on_cash"].includes(outputMetric);
  const missingPurchasePrice = needsPurchasePrice && (!currentAssumptions.purchase_price || currentAssumptions.purchase_price <= 0);

  // Compute min/max for color gradient
  let valMin = Infinity, valMax = -Infinity;
  if (result) {
    for (const row of result.results) {
      for (const v of row) {
        if (v != null) { valMin = Math.min(valMin, v); valMax = Math.max(valMax, v); }
      }
    }
  }

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      <button onClick={handleToggle} className="w-full px-5 py-3 flex items-center justify-between text-left">
        <h3 className="text-sm font-semibold text-bark-700">Sensitivity Analysis</h3>
        {open ? <ChevronDown className="w-4 h-4 text-bark-400" /> : <ChevronRight className="w-4 h-4 text-bark-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-grove-200 pt-4">
          {/* Controls */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
            <div>
              <label className="block text-[10px] font-semibold uppercase text-bark-400 mb-1">Row Variable</label>
              <select value={rowVar} onChange={e => handleVarChange("row", e.target.value)}
                className="w-full px-2 py-1.5 border border-grove-300 rounded text-[12px] bg-grove-50">
                {INPUT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="flex gap-1 mt-1">
                <input placeholder="Min" value={rowMin} onChange={e => setRowMin(e.target.value)}
                  className="w-1/3 px-1 py-1 border border-grove-200 rounded text-[11px] tabular-nums" />
                <input placeholder="Max" value={rowMax} onChange={e => setRowMax(e.target.value)}
                  className="w-1/3 px-1 py-1 border border-grove-200 rounded text-[11px] tabular-nums" />
                <input placeholder="Step" value={rowStep} onChange={e => setRowStep(e.target.value)}
                  className="w-1/3 px-1 py-1 border border-grove-200 rounded text-[11px] tabular-nums" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-bark-400 mb-1">Column Variable</label>
              <select value={colVar} onChange={e => handleVarChange("col", e.target.value)}
                className="w-full px-2 py-1.5 border border-grove-300 rounded text-[12px] bg-grove-50">
                <option value="">None (1D)</option>
                {INPUT_OPTIONS.filter(o => o.value !== rowVar).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {colVar && (
                <div className="flex gap-1 mt-1">
                  <input placeholder="Min" value={colMin} onChange={e => setColMin(e.target.value)}
                    className="w-1/3 px-1 py-1 border border-grove-200 rounded text-[11px] tabular-nums" />
                  <input placeholder="Max" value={colMax} onChange={e => setColMax(e.target.value)}
                    className="w-1/3 px-1 py-1 border border-grove-200 rounded text-[11px] tabular-nums" />
                  <input placeholder="Step" value={colStep} onChange={e => setColStep(e.target.value)}
                    className="w-1/3 px-1 py-1 border border-grove-200 rounded text-[11px] tabular-nums" />
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-bark-400 mb-1">Output Metric</label>
              <select value={outputMetric} onChange={e => setOutputMetric(e.target.value)}
                className="w-full px-2 py-1.5 border border-grove-300 rounded text-[12px] bg-grove-50">
                {OUTPUT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button onClick={handleRun} disabled={loading}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-forest-600 text-white text-[12px] font-semibold rounded-lg hover:bg-forest-500 disabled:opacity-50 transition-all">
                {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
                {loading ? "Running..." : "Run Analysis"}
              </button>
              {result && (
                <button onClick={exportResults} className="p-1.5 text-bark-400 hover:text-forest-600 transition-colors" title="Export CSV">
                  <Download className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {missingPurchasePrice && (
            <p className="text-xs text-honey-700 bg-honey-50 border border-honey-200 rounded-lg px-3 py-2">
              Set a purchase price in Deal Assumptions before running — IRR, equity multiple, and NPV require it.
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Results Matrix */}
          {result && (
            <div className="overflow-x-auto rounded-lg border border-grove-200">
              <table className="text-[11px] tabular-nums border-collapse w-full">
                <thead>
                  <tr className="bg-grove-50">
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase text-bark-400 border-b border-grove-200 min-w-[100px]">
                      {INPUT_OPTIONS.find(o => o.value === result.row_variable)?.label}
                    </th>
                    {result.col_values ? result.col_values.map((cv, ci) => (
                      <th key={ci} className={`px-3 py-2 text-right text-[10px] font-semibold uppercase border-b border-grove-200 min-w-[80px] ${
                        result.current_col_value != null && Math.abs(cv - result.current_col_value) < 1e-4 ? "text-forest-700 bg-forest-50" : "text-bark-400"
                      }`}>
                        {fmtInputVal(cv, result.col_variable!)}
                      </th>
                    )) : (
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-bark-400 border-b border-grove-200 min-w-[80px]">
                        {OUTPUT_OPTIONS.find(o => o.value === outputMetric)?.label}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {result.row_values.map((rv, ri) => {
                    const isCurrentRow = Math.abs(rv - result.current_row_value) < 1e-4;
                    return (
                      <tr key={ri}>
                        <td className={`px-3 py-1.5 font-medium whitespace-nowrap border-b border-grove-100 ${isCurrentRow ? "text-forest-700 bg-forest-50" : "text-bark-500"}`}>
                          {fmtInputVal(rv, result.row_variable)}
                        </td>
                        {result.results[ri].map((v, ci) => {
                          const isCurrentCol = result.col_values
                            ? result.current_col_value != null && Math.abs(result.col_values[ci] - result.current_col_value) < 1e-4
                            : true;
                          const isCurrent = isCurrentRow && isCurrentCol;
                          return (
                            <td key={ci}
                              className={`px-3 py-1.5 text-right whitespace-nowrap border-b border-grove-100 min-w-[80px] ${isCurrent ? "ring-2 ring-inset ring-forest-700 font-bold" : ""}`}
                              style={cellStyle(v, valMin, valMax)}
                            >
                              {fmtOutputVal(v, outputMetric)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
